import {
  Inject,
  Injectable,
  makeStateKey,
  Optional,
  Renderer2,
  RESPONSE_INIT,
  StateKey,
  TransferState
} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {StorageService} from './storage.service';
import {NavigationService} from './navigation.service';
import {ToastService} from './toast.service';
import {PlatformService} from './platform.service';
import {StorableService} from './storable.service';
import {Route} from '../classes/route';
import {RouteDTO} from '../dto/route-dto';
import {RouteGpx} from '../classes/route-gpx';
import {RouteGpxDTO} from '../dto/route-gpx-dto';
import {RouteMarker} from '../interfaces/route-marker';
import {BehaviorSubject, catchError, from, map, Observable, tap} from 'rxjs';
import {environment} from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RouteService extends StorableService<RouteDTO, Route> {
  private _markers: RouteMarker[] = [];
  private _markers$: BehaviorSubject<RouteMarker[]> = new BehaviorSubject<RouteMarker[]>([]);

  private _storage: StorageService;

  constructor(http: HttpClient, storageService: StorageService, transferState: TransferState,
              navigationService: NavigationService, toastService: ToastService, platformService: PlatformService,
              @Optional() @Inject(RESPONSE_INIT) response?: ResponseInit) {
    super(http, storageService, transferState, navigationService, toastService, platformService, response);
    this._storage = storageService;
  }

  readonly gpxTableName = 'route-gpx';

  readonly tableName = 'routes';

  readonly endpoint = '/routes';

  get stateKey(): StateKey<RouteDTO[]> {
    return makeStateKey('routes');
  }

  protected createData(dto: RouteDTO): Route {
    return new Route(dto);
  }

  getMarkers(): BehaviorSubject<RouteMarker[]> {
    return this._markers$;
  }

  addMarkers(renderer2: Renderer2) {
    const grouped: RouteMarker[] = [];
    this.data.forEach(route => {
      const existing = grouped.find(group =>
        JSON.stringify(group.googleMapsLatLngLiteral) === JSON.stringify(route.googleMapsLatLngLiteral));
      if (existing) {
        existing.routes.push(route);
        return;
      }
      grouped.push({
        id: grouped.length,
        googleMapsLatLngLiteral: route.googleMapsLatLngLiteral,
        routes: [route],
      });
    });

    grouped.forEach(group => {
      const element = renderer2.createElement('div');
      if (group.routes.length > 1) {
        element.style.position = 'relative';
        element.innerHTML = `<span class="cluster-content">${group.routes.length}</span><img src="/svg/categories/markers/default.svg" alt="" class="w-8"/>`;
      } else {
        element.innerHTML = `<img src="/svg/categories/markers/routes.svg" alt="" class="w-8"/>`;
      }
      group.markerContent = element;
    });

    this._markers = grouped;
    this._markers$.next(this._markers);
  }

  clearMarkers() {
    this._markers = [];
    this._markers$.next(this._markers);
  }

  getGpx(id: number): Observable<string> {
    return this.http.get(`${environment.api.url}/routes/${id}/gpx`, {
      responseType: 'text',
      headers: {['Accept']: 'application/gpx+xml'},
    }).pipe(
      tap(gpx => void this._cacheGpx(id, gpx)),
      catchError(() => from(this._getCachedGpx(id)).pipe(
        map(cached => {
          if (cached === null) {
            throw new Error(`No cached GPX available for route ${id}`);
          }
          return cached;
        }),
      )),
    );
  }

  private async _getCachedGpx(id: number): Promise<string | null> {
    const dtos = await this._getGpxDataFromStorage();
    return dtos.find(dto => dto.id === id)?.gpx ?? null;
  }

  private async _cacheGpx(id: number, gpx: string): Promise<void> {
    const dtos = await this._getGpxDataFromStorage();
    const routeGpxByRouteId = new Map<number, RouteGpx>(dtos.map(dto => [dto.id, new RouteGpx(dto)]));
    routeGpxByRouteId.set(id, new RouteGpx({id, gpx}));
    await this._storage.set(this.gpxTableName, Array.from(routeGpxByRouteId.values()));
  }

  private async _getGpxDataFromStorage(): Promise<RouteGpxDTO[]> {
    const stringified = await this._storage.get(this.gpxTableName);
    try {
      const dtos: RouteGpxDTO[] = JSON.parse(stringified);
      return Array.isArray(dtos) ? dtos : [];
    } catch {
      return [];
    }
  }

  getRoutesForPointOfInterest(pointOfInterestId: number): Route[] {
    return this.data.filter(route => route.pointOfInterestIds.includes(pointOfInterestId));
  }
}
